/**
 * Our Brands – Reveal Animation
 *
 * Cascading scroll-triggered reveal:
 *   1. Heading fades/slides up
 *   2. Logo buttons stagger in from below
 *   3. Content wrapper (active panel) fades/slides up
 *
 * Respects prefers-reduced-motion, includes GSAP polling + fallback timer,
 * and cleans up ScrollTrigger on disconnect.
 */

class OurBrandsReveal extends HTMLElement {
  constructor() {
    super();
    this.timeline = null;
    this.fallbackTimer = null;
  }

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.showFallback();
      return;
    }

    if (document.readyState === 'complete') {
      this.init();
    } else {
      window.addEventListener('load', () => this.init());
    }

    /* Safety fallback — reveal after 5 s only if GSAP never initialises */
    this.fallbackTimer = setTimeout(() => this.showFallback(), 5000);
  }

  /* -------- GSAP helpers (same pattern as other animation files) -------- */

  getGSAP() {
    if (typeof window !== 'undefined' && window.gsap && typeof window.gsap.to === 'function') {
      return window.gsap;
    }
    if (typeof window !== 'undefined' && window.GSAP && typeof window.GSAP.to === 'function') {
      return window.GSAP;
    }
    if (typeof window !== 'undefined' && window.GreenSockGlobals && window.GreenSockGlobals.gsap) {
      return window.GreenSockGlobals.gsap;
    }
    if (typeof gsap !== 'undefined' && typeof gsap.to === 'function') {
      return gsap;
    }
    return null;
  }

  waitForGSAP() {
    return new Promise((resolve) => {
      if (this.getGSAP()) {
        resolve();
        return;
      }

      /* Poll for GSAP availability — CDN defer scripts may not have executed yet */
      var attempts = 0;
      var check = setInterval(() => {
        attempts++;
        if (this.getGSAP() || attempts > 50) { // Max 5 seconds polling
          clearInterval(check);
          resolve();
        }
      }, 100);
    });
  }

  /* -------- Initialisation -------- */

  async init() {
    await this.waitForGSAP();
    var gsapLib = this.getGSAP();

    if (!gsapLib) {
      this.showFallback();
      return;
    }

    /* GSAP loaded — cancel the safety fallback */
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }

    var sectionId = this.getAttribute('data-section-id');
    var section = document.getElementById('OurBrands-' + sectionId);
    if (!section) return;

    /* Gather targets */
    var heading = section.querySelector('.our-brands__heading');
    var logoButtons = section.querySelectorAll('.our-brands__logo-button');
    var contentWrapper = section.querySelector('.our-brands__content-wrapper');

    /* Nothing to animate */
    if (!heading && !logoButtons.length && !contentWrapper) {
      this.showFallback();
      return;
    }

    /* Register ScrollTrigger if available */
    var hasScrollTrigger = typeof ScrollTrigger !== 'undefined' ||
                           (gsapLib.plugins && gsapLib.plugins.scrollTrigger);

    if (hasScrollTrigger && gsapLib.registerPlugin) {
      try {
        gsapLib.registerPlugin(ScrollTrigger || gsapLib.plugins.scrollTrigger);
      } catch (e) {
        // Already registered
      }
    }

    /* Build a timeline so the three groups animate sequentially */
    var triggerEl = section.querySelector('.our-brands__container') || section;

    if (hasScrollTrigger) {
      this.timeline = gsapLib.timeline({
        scrollTrigger: {
          trigger: triggerEl,
          start: 'top 80%',
          once: true
        }
      });
    } else {
      /* Fallback: use IntersectionObserver to start the timeline */
      this.timeline = gsapLib.timeline({ paused: true });

      var self = this;
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            self.timeline.play();
            observer.disconnect();
          }
        });
      }, { threshold: 0.15 });
      observer.observe(triggerEl);
    }

    /* 1. Heading — fade & slide up */
    if (heading) {
      this.timeline.fromTo(heading,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' },
        0 /* start immediately */
      );
    }

    /* 2. Logo buttons — stagger in */
    if (logoButtons.length) {
      this.timeline.fromTo(logoButtons,
        { opacity: 0, y: 30, scale: 0.92 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          stagger: 0.08,
          ease: 'back.out(1.4)'
        },
        heading ? 0.25 : 0 /* slight overlap with heading */
      );
    }

    /* 3. Content wrapper — fade & slide up */
    if (contentWrapper) {
      this.timeline.fromTo(contentWrapper,
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' },
        heading ? 0.45 : 0.2 /* starts shortly after logos begin */
      );
    }
  }

  /* -------- Fallback (no GSAP / reduced-motion) -------- */

  showFallback() {
    var sectionId = this.getAttribute('data-section-id');
    var section = document.getElementById('OurBrands-' + sectionId);
    if (!section) return;

    var els = section.querySelectorAll(
      '.our-brands__heading, .our-brands__logo-button, .our-brands__content-wrapper'
    );
    els.forEach(function (el) {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
  }

  /* -------- Cleanup -------- */

  disconnectedCallback() {
    if (this.timeline) {
      if (this.timeline.scrollTrigger) this.timeline.scrollTrigger.kill();
      if (this.timeline.kill) this.timeline.kill();
    }
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
    }
  }
}

if (!customElements.get('our-brands-reveal')) {
  customElements.define('our-brands-reveal', OurBrandsReveal);
}


